import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const testimonials = [
  {
    name: '张先生',
    role: '互联网从业者',
    content: '九色导航是我每天打开浏览器的第一个页面，分类清晰，网站齐全，大大提高了我的工作效率。强烈推荐给每一位网民！',
    avatar: '',
  },
  {
    name: '李女士',
    role: '自由职业者',
    content: '用了九色导航之后，再也不用手动输入网址了。界面简洁美观，网站更新也很及时，体验非常棒。',
    avatar: '',
  },
  {
    name: '王同学',
    role: '大学生',
    content: '学习资料、娱乐资源、实用工具一应俱全，非常方便。已经推荐给全班同学使用了，大家都说好！',
    avatar: '',
  },
  {
    name: '赵先生',
    role: '企业管理者',
    content: '界面设计专业，分类合理，没有乱七八糟的广告。作为浏览器首页使用体验极佳，值得信赖。',
    avatar: '',
  },
  {
    name: '陈女士',
    role: '设计师',
    content: '九色导航的视觉设计很用心，色彩搭配舒适，是颜值和实用性兼备的导航网站。每天都在用！',
    avatar: '',
  },
  {
    name: '刘同学',
    role: '研究生',
    content: '写论文查资料、找工具网站很方便，学习区的资源特别丰富。已经离不开九色导航了！',
    avatar: '',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9re8aue3oq4jqumu0ag', {
        method: 'POST',
        body: formData,
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch {}

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch (_err) {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '九色导航是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '九色导航是专业的一站式网址导航平台，汇聚海量优质网站资源，涵盖娱乐、购物、新闻、工具、学习等九大分类，帮助用户快速找到所需网站。',
        },
      },
      {
        '@type': 'Question',
        name: '如何将九色导航设置为浏览器首页？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '打开浏览器设置，找到「启动时打开」或「主页」设置选项，将九色导航的网址设置为您的浏览器首页即可。',
        },
      },
      {
        '@type': 'Question',
        name: '九色导航收费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '九色导航完全免费使用，无需注册，无需付费，打开即用。我们致力于为所有网民提供最便捷的上网导航服务。',
        },
      },
      {
        '@type': 'Question',
        name: '如何推荐网站收录？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过关于我们页面的联系表单提交网站收录申请，我们的团队会审核后决定是否收录。',
        },
      },
      {
        '@type': 'Question',
        name: '九色导航有APP吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '目前九色导航主要以网页版为主，您可以在手机浏览器中打开使用。未来我们会考虑推出移动APP版本。',
        },
      },
    ],
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '九色导航',
    url: 'https://www.jiusenavigation.com',
    description: '专业的一站式网址导航平台，汇聚海量优质网站资源',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@jiusenavigation.com',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 品牌介绍与联系方式 | 九色导航"
        description="了解九色导航的品牌故事、核心优势与用户口碑。九色导航是专业的一站式网址导航平台，致力于让上网更简单、更高效。联系我们获取更多帮助。"
        keywords="九色导航介绍,网址导航品牌,导航网站推荐,关于九色导航,联系我们,网站收录"
        canonicalUrl="/about"
        schemaJson={{ ...orgSchema, ...faqSchema }}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <header className="pt-24 pb-8 px-4 md:px-6 bg-background-100/50">
          <div className="max-w-screen-2xl mx-auto">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">
              关于九色导航
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              了解我们的品牌故事、核心优势与用户口碑
            </p>
          </div>
        </header>

        <main>
          {/* Brand Intro */}
          <section className="py-14 px-4 md:px-6">
            <div className="max-w-screen-2xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-5">
                    品牌故事
                  </h2>
                  <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      九色导航诞生于2024年，由一群热爱互联网的产品工程师倾心打造。我们深知在信息爆炸的时代，快速找到所需网站是每个网民的基本需求。然而，市面上的导航网站要么广告泛滥、要么界面陈旧，无法提供优质的用户体验。
                    </p>
                    <p>
                      于是，我们决定打造一款真正以用户为中心的网址导航平台——<strong>九色导航</strong>应运而生。九色代表了我们涵盖的九大核心分类：娱乐休闲、购物消费、新闻资讯、实用工具、学习教育、社交社区、生活服务、科技数码、财经金融。每一种颜色，代表一个精彩的上网世界。
                    </p>
                    <p>
                      我们坚持<strong>简洁设计、精选内容、持续更新</strong>的三大原则，不断优化产品体验，为用户提供最优质的上网导航服务。目前，九色导航已经收录了数百个经过精心筛选的优质网站，每天为数万用户提供便捷的上网入口。
                    </p>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20with%20laptop%20displaying%20colorful%20website%20dashboard%20interface%2C%20warm%20natural%20lighting%20from%20window%2C%20clean%20wooden%20desk%20with%20coffee%20and%20plant%2C%20cozy%20creative%20office%20atmosphere&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="九色导航品牌故事"
                    className="w-full h-full object-cover"
                    title="九色导航 - 品牌故事"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Core Advantages */}
          <section className="py-14 px-4 md:px-6 bg-background-100/50">
            <div className="max-w-screen-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-3">
                  为什么选择九色导航？
                </h2>
                <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                  四大核心优势，打造最优质的网址导航体验
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[
                  { icon: 'ri-layout-grid-line', title: '九大分类', desc: '精心划分九大核心分类，覆盖上网需求的方方面面，一目了然，快速定位。', color: 'primary' },
                  { icon: 'ri-shield-check-line', title: '精选内容', desc: '每一网站都经过人工审核筛选，确保资源优质可靠，拒绝垃圾网站和恶意链接。', color: 'accent' },
                  { icon: 'ri-refresh-line', title: '持续更新', desc: '定期更新网站资源库，及时收录新兴优质网站，剔除失效链接，保持内容新鲜。', color: 'secondary' },
                  { icon: 'ri-heart-2-line', title: '永久免费', desc: '无需注册、无需付费，打开即用。以用户价值为核心理念，永久免费提供服务。', color: 'primary' },
                ].map((item) => {
                  const colorClasses = {
                    primary: { bg: 'bg-primary-100', text: 'text-primary-700', iconBg: 'bg-primary-100' },
                    accent: { bg: 'bg-accent-100', text: 'text-accent-700', iconBg: 'bg-accent-100' },
                    secondary: { bg: 'bg-secondary-100', text: 'text-secondary-700', iconBg: 'bg-secondary-100' },
                  }[item.color] || { bg: 'bg-primary-100', text: 'text-primary-700', iconBg: 'bg-primary-100' };

                  return (
                    <div key={item.title} className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
                      <div className={`w-12 h-12 rounded-xl ${colorClasses.iconBg} flex items-center justify-center mb-4`}>
                        <i className={`${item.icon} text-xl ${colorClasses.text}`}></i>
                      </div>
                      <h3 className="font-semibold text-sm text-foreground-900 mb-2">{item.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Statistics */}
          <section className="py-14 px-4 md:px-6">
            <div className="max-w-screen-2xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: '66+', label: '收录网站', icon: 'ri-global-line', color: 'primary' },
                  { value: '9', label: '核心分类', icon: 'ri-grid-line', color: 'accent' },
                  { value: '10万+', label: '日访问量', icon: 'ri-user-line', color: 'secondary' },
                  { value: '100%', label: '用户满意度', icon: 'ri-star-line', color: 'primary' },
                ].map((stat) => {
                  const colorMap: Record<string, { text: string }> = {
                    primary: { text: 'text-primary-600' },
                    accent: { text: 'text-accent-600' },
                    secondary: { text: 'text-secondary-600' },
                  };
                  const c = colorMap[stat.color] || colorMap.primary;
                  return (
                    <div key={stat.label} className="text-center p-6 rounded-2xl bg-background-100/50 border border-background-200/60">
                      <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-background-50 flex items-center justify-center">
                        <i className={`${stat.icon} text-lg ${c.text}`}></i>
                      </div>
                      <div className={`text-2xl md:text-3xl font-heading ${c.text} mb-1`}>
                        {stat.value}
                      </div>
                      <div className="text-xs text-foreground-500">{stat.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-14 px-4 md:px-6 bg-background-100/50">
            <div className="max-w-screen-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-3">
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                  听听用户怎么说——真实评价，口碑见证
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {testimonials.map((t) => (
                  <div key={t.name} className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-primary-600">{t.name.charAt(0)}</span>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                        <div className="text-xs text-foreground-400">{t.role}</div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                    <div className="flex items-center gap-0.5 mt-3">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-xs text-amber-400"></i>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-14 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500">
                  快速了解九色导航
                </p>
              </div>
              <div className="space-y-3">
                {([
                  { q: '九色导航是什么？', a: '九色导航是专业的一站式网址导航平台，汇聚海量优质网站资源，涵盖娱乐、购物、新闻、工具、学习等九大分类，帮助用户快速找到所需网站。' },
                  { q: '如何将九色导航设置为浏览器首页？', a: '打开浏览器设置，找到「启动时打开」或「主页」设置选项，将九色导航的网址设置为您的浏览器首页即可。具体操作方法可参考我们文章中心的设置指南。' },
                  { q: '九色导航收费吗？', a: '九色导航完全免费使用，无需注册，无需付费，打开即用。我们致力于为所有网民提供最便捷的上网导航服务。' },
                  { q: '如何推荐网站收录？', a: '您可以通过下方的联系表单提交网站收录申请，我们的审核团队会在3个工作日内完成审核。' },
                  { q: '九色导航有APP吗？', a: '目前九色导航主要以网页版为主，您可以在手机浏览器中打开使用，体验同样流畅。未来我们会根据用户需求考虑推出移动APP版本。' },
                ]).map((faq) => (
                  <details key={faq.q} className="group rounded-2xl bg-background-100/50 border border-background-200/60 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <span className="text-sm font-medium text-foreground-900">{faq.q}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="py-14 px-4 md:px-6 bg-background-100/50">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500">
                  有任何问题或建议，欢迎随时联系我们。我们会在第一时间回复您。
                </p>
              </div>

              <form data-readdy-form onSubmit={handleContactSubmit} className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-8 space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100/50 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100/50 transition-all"
                    placeholder="请输入您的邮箱地址"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">主题</label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100/50 transition-all cursor-pointer"
                  >
                    <option value="">请选择咨询主题</option>
                    <option value="网站收录">网站收录申请</option>
                    <option value="意见建议">意见建议</option>
                    <option value="商务合作">商务合作</option>
                    <option value="问题反馈">问题反馈</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100/50 transition-all resize-none"
                    placeholder="请输入留言内容（500字以内）"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hpt-field"
                />

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'loading' ? '提交中...' : '提交留言'}
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-accent-600 text-center">感谢您的留言，我们会尽快回复！</p>
                )}
                {formStatus === 'error' && formError && (
                  <p className="text-sm text-red-500 text-center">{formError}</p>
                )}
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}